'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { z } from '@/lib/validation/zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { type MouseEvent, useEffect, useId, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useLegalDialog } from '@/components/integrations/legal/legal-dialog-provider'
import { FormErrorBanner } from '@/components/site/forms/form-error-banner'
import { Button } from '@/components/ui/button'
import { Checkbox, CheckboxField } from '@/components/ui/checkbox'
import { ErrorMessage, Field, Label } from '@/components/ui/fieldset'
import { Input } from '@/components/ui/input'
import { analytics } from '@/lib/analytics/posthog'

import { formatMessage } from '@/components/site/forms/form-helpers'
import { attributionForSignup } from '@/lib/analytics/attribution'
import { applyApiErrors, LIMITS, requestJson } from './register-helpers'
import type { RegisterCopy, RegisterData, RegisterUi } from './register-types'

type RegisterPasswordResponse = Partial<RegisterData> & { eventId?: string; hash?: string }

type PasswordStepProps = {
  copy: RegisterCopy['password']
  ui: RegisterUi
  defaultValues: RegisterData
  onNext: (data: Partial<RegisterData>) => void
  onPrevious: (data: Partial<RegisterData>) => void
}

export function RegisterPasswordStep({ copy, ui, defaultValues, onNext, onPrevious }: PasswordStepProps) {
  const { openLegal } = useLegalDialog()
  const termsId = useId()
  const passwordId = useId()
  const passwordConfirmationId = useId()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const schema = useMemo(
    () =>
      z
        .object({
          password: z
            .string()
            .min(1, ui.validation.passwordRequired)
            .min(LIMITS.passwordMin, formatMessage(ui.validation.passwordMin, { min: LIMITS.passwordMin }))
            .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, ui.validation.passwordPattern),
          passwordConfirmation: z.string().min(1, ui.validation.passwordConfirmationRequired),
          terms: z.boolean().refine((value) => value, { message: ui.validation.termsRequired }),
        })
        .superRefine((values, context) => {
          if (values.password !== values.passwordConfirmation) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: ui.validation.passwordMismatch,
              path: ['passwordConfirmation'],
            })
          }
        }),
    [
      ui.validation.passwordRequired,
      ui.validation.passwordMin,
      ui.validation.passwordPattern,
      ui.validation.passwordConfirmationRequired,
      ui.validation.termsRequired,
      ui.validation.passwordMismatch,
    ],
  )

  const {
    register,
    handleSubmit,
    setFocus,
    setError,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<{ password: string; passwordConfirmation: string; terms: boolean }>({
    defaultValues: {
      password: defaultValues.password || '',
      passwordConfirmation: defaultValues.passwordConfirmation || '',
      terms: Boolean(defaultValues.terms),
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    setFocus('password')
  }, [setFocus])

  const onSubmit = handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      const data = await requestJson<RegisterPasswordResponse>('register', {
        method: 'POST',
        // The campaign this session arrived through: the Manager reads it here and writes it to
        // the client's permanent acquisition columns. Sent on the lead call too, in case the
        // signup is never finished.
        body: JSON.stringify({ ...defaultValues, ...attributionForSignup(), ...values }),
      })

      analytics.capture('signup_credentials_submitted', { conversion_id: data?.eventId })

      onNext({ ...values, ...(data ?? {}) })
    } catch (error) {
      applyApiErrors(error, setError, setErrorMessage, ui.validation.submitFailed)
    }
  })

  const onPreviousClick = () => {
    onPrevious(getValues())
  }

  const labelClass = 'text-mist-900 dark:text-mist-100'
  const termsClass = '!text-mist-700 dark:!text-mist-300'
  const buttonColor = 'dark/zinc'
  const linkClass =
    'cursor-pointer underline underline-offset-4 text-mist-700 transition hover:text-mist-950 dark:text-mist-300 dark:hover:text-white'
  const handleLegalClick = (event: MouseEvent<HTMLButtonElement>, key: 'legal-terms' | 'legal-privacy') => {
    event.preventDefault()
    event.stopPropagation()
    openLegal(key)
  }

  return (
    <form className="space-y-5" noValidate onSubmit={onSubmit}>
      {errorMessage && <FormErrorBanner className="font-medium">{errorMessage}</FormErrorBanner>}

      <Field>
        <Label htmlFor={passwordId} className={labelClass} required>
          {ui.fields.password}
        </Label>
        <Input
          id={passwordId}
          type="password"
          autoComplete="new-password"
          placeholder={ui.placeholders.signup.password.password}
          {...register('password')}
          invalid={Boolean(errors.password)}
          aria-invalid={Boolean(errors.password)}
          aria-describedby={errors.password ? `${passwordId}-error` : undefined}
          disabled={isSubmitting}
        />
        {errors.password?.message && <ErrorMessage id={`${passwordId}-error`}>{errors.password.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label htmlFor={passwordConfirmationId} className={labelClass} required>
          {ui.fields.passwordConfirmation}
        </Label>
        <Input
          id={passwordConfirmationId}
          type="password"
          autoComplete="new-password"
          placeholder={ui.placeholders.signup.password.passwordConfirmation}
          {...register('passwordConfirmation')}
          invalid={Boolean(errors.passwordConfirmation)}
          aria-invalid={Boolean(errors.passwordConfirmation)}
          aria-describedby={errors.passwordConfirmation ? `${passwordConfirmationId}-error` : undefined}
          disabled={isSubmitting}
        />
        {errors.passwordConfirmation?.message && (
          <ErrorMessage id={`${passwordConfirmationId}-error`}>{errors.passwordConfirmation.message}</ErrorMessage>
        )}
      </Field>

      <CheckboxField>
        <Controller
          name="terms"
          control={control}
          render={({ field }) => (
            <Checkbox
              id={termsId}
              name={field.name}
              ref={field.ref}
              checked={Boolean(field.value)}
              onBlur={field.onBlur}
              onChange={(event) => field.onChange(event.target.checked)}
              disabled={isSubmitting}
            />
          )}
        />
        <Label htmlFor={termsId} className={`!text-sm/6 !font-normal ${termsClass}`} required>
          {copy.terms.prefix}{' '}
          <button type="button" className={linkClass} onClick={(event) => handleLegalClick(event, 'legal-terms')}>
            {ui.labels.pages['legal-terms']}
          </button>{' '}
          {copy.terms.middle}{' '}
          {/* The button renders inline-block, which gives the browser a break opportunity right
              after it — letting the suffix and required asterisk wrap onto a line of their own. */}
          <span className="whitespace-nowrap">
            <button type="button" className={linkClass} onClick={(event) => handleLegalClick(event, 'legal-privacy')}>
              {ui.labels.pages['legal-privacy']}
            </button>
            {copy.terms.suffix}
          </span>
        </Label>
        {errors.terms?.message && (
          <ErrorMessage className="col-start-2 row-start-2">{errors.terms.message}</ErrorMessage>
        )}
      </CheckboxField>

      <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-between">
        <Button type="submit" size="lg" color={buttonColor} disabled={isSubmitting}>
          {copy.actions.submit}
        </Button>
        <Button
          type="button"
          size="lg"
          plain
          className="!text-mist-700 hover:!text-mist-950 dark:!text-mist-300 dark:hover:!text-white"
          onClick={onPreviousClick}
          disabled={isSubmitting}
        >
          {ui.actions.back}
        </Button>
      </div>
    </form>
  )
}
